/**
 * Termine, die mit einem Projekt oder einer Aufgabe verknuepft sind
 * (F-04 d, Leitlinie 5). Ein Klick oeffnet den Termin im Kalender.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { useNavigate } from 'react-router-dom';

import { getOccurrences } from '@/db/repositories/events';
import { verknuepfteTermine, type TerminFilter } from '@/modules/calendar/lib/linkedOccurrences';
import { useCalendarStore } from '@/store/calendar';
import { useUiStore } from '@/store/ui';
import { addDays, datePart, formatDe, formatTime, now, today } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';
import type { CalendarOccurrence, LocalDateTime } from '@/domain/types';

/** Vergangenheit und Zukunft, die betrachtet werden. */
const ZURUECK = 90;
const VORAUS = 365;

export function VerknuepfteTermine({ filter, leerText, kompakt = false, onGeoeffnet }: {
  filter: TerminFilter;
  /** Z. B. einen Dialog schliessen, bevor der Kalender aufgeht. */
  onGeoeffnet?: () => void;
  /** `null`: ohne Termine gar nichts zeigen (z. B. im Aufgaben-Dialog). */
  leerText: string | null;
  kompakt?: boolean;
}) {
  const navigate = useNavigate();
  const setView = useUiStore((s) => s.setCalendarView);
  const setAnchor = useCalendarStore((s) => s.setAnchor);
  const select = useCalendarStore((s) => s.select);
  const heute = today();
  const alle = useLiveQuery(() => getOccurrences({
    from: `${addDays(heute, -ZURUECK)}T00:00` as LocalDateTime,
    to: `${addDays(heute, VORAUS)}T00:00` as LocalDateTime,
  }), [heute], [] as CalendarOccurrence[]);
  const { kommend, vergangen } = verknuepfteTermine(alle, filter, now());

  function oeffnen(o: CalendarOccurrence) {
    setView('tag');
    setAnchor(datePart(String(o.start)));
    select(o);
    onGeoeffnet?.();
    navigate('/kalender');
  }

  if (kommend.length === 0 && vergangen.length === 0) {
    return leerText === null ? null : <p className="text-sm text-ink-faint">{leerText}</p>;
  }

  const zeile = (o: CalendarOccurrence, alt: boolean) => (
    <li key={o.occurrenceId}>
      <button type="button" onClick={() => oeffnen(o)}
        className={cn('flex w-full items-center gap-2 rounded px-1 py-0.5 text-left text-sm hover:bg-surface-raised', alt && 'text-ink-faint')}>
        <span className="w-24 shrink-0 tabular-nums text-ink-faint">
          {formatDe(String(o.start), 'EEEEEE dd.MM.')}{o.allDay ? '' : ` ${formatTime(String(o.start) as LocalDateTime)}`}
        </span>
        <span className="min-w-0 flex-1 truncate">{o.title || 'Ohne Titel'}</span>
      </button>
    </li>
  );

  return (
    <div className="flex flex-col gap-1.5">
      {kommend.length > 0 && <ul aria-label="Kommende Termine" className="flex flex-col">{kommend.slice(0, kompakt ? 3 : 5).map((o) => zeile(o, false))}</ul>}
      {vergangen.length > 0 && (
        <>
          <p className="text-[11px] text-ink-faint">Zuletzt</p>
          <ul aria-label="Vergangene Termine" className="flex flex-col">{vergangen.slice(0, kompakt ? 2 : 3).map((o) => zeile(o, true))}</ul>
        </>
      )}
    </div>
  );
}
